'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface FormData {
  name: string;
  email: string;
  company: string;
  inquiryType: string;
  message: string;
}

/** Shared field chrome — dark navy surface, blue focus ring. */
const inputClass =
  'w-full rounded-lg border border-white/15 bg-[#0a1626]/80 py-3 pl-11 pr-4 text-[0.9375rem] text-white placeholder:text-slate-500 transition-[border-color,box-shadow] duration-base ease-out focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40';

const cardSurface = {
  background: 'linear-gradient(160deg, rgba(13,28,54,0.72) 0%, rgba(10,19,34,0.62) 100%)',
};

const ContactForm = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    inquiryType: 'general',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitError, setSubmitError] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setSubmitError('');

    try {
      const response = await fetch('/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          'form-name': 'contact',
          'bot-field': '',
          name: formData.name,
          email: formData.email,
          company: formData.company,
          inquiryType: formData.inquiryType,
          message: formData.message,
        }).toString(),
      });

      if (!response.ok) {
        throw new Error('Unable to send message right now.');
      }

      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        inquiryType: 'general',
        message: '',
      });

      setTimeout(() => setSubmitStatus('idle'), 5000);
    } catch (error) {
      setIsSubmitting(false);
      setSubmitStatus('error');
      setSubmitError(error instanceof Error ? error.message : 'Unable to send message right now.');
    }
  };

  return (
    <section id="contact-form" className="section relative overflow-hidden bg-ink text-white">
      {/* Restrained blue cast + faint graphene dot lattice at the edges. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(55rem_28rem_at_10%_-8%,rgba(14,165,233,0.1),transparent_60%),radial-gradient(50rem_26rem_at_95%_108%,rgba(37,99,235,0.09),transparent_60%),linear-gradient(172deg,#0a1526_0%,#060c18_55%,#0a1729_100%)]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 left-0 w-36 opacity-[0.06]"
        style={{
          backgroundImage: 'radial-gradient(rgba(56,189,248,0.9) 1px, transparent 1px)',
          backgroundSize: '17px 17px',
          maskImage: 'linear-gradient(90deg, black, transparent)',
          WebkitMaskImage: 'linear-gradient(90deg, black, transparent)',
        }}
      />

      <div className="container-page relative">
        <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,54fr)_minmax(0,46fr)] lg:gap-10">
          {/* ── Send Us a Message panel ─────────────────────────────────── */}
          <div
            data-reveal="left"
            className="min-w-0 rounded-2xl border border-accent/25 p-6 shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_24px_60px_rgba(2,8,23,0.5),0_0_36px_rgba(14,165,233,0.08)] sm:p-8 lg:p-9"
            style={{
              background: 'linear-gradient(160deg, rgba(13,28,54,0.8) 0%, rgba(8,16,32,0.74) 100%)',
            }}
          >
            <div className="flex items-start gap-4 sm:items-center sm:gap-5">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-accent/40 bg-accent/10 text-accent shadow-[0_0_20px_rgba(14,165,233,0.18),inset_0_0_14px_rgba(14,165,233,0.08)] sm:h-16 sm:w-16">
                <Icon name="PaperAirplaneIcon" size={28} />
              </span>
              <div className="min-w-0">
                <h2 className="text-2xl font-bold tracking-tight text-white sm:text-[1.75rem]">
                  Send Us a Message
                </h2>
                <p className="mt-1.5 text-[0.9375rem] leading-relaxed text-slate-300 sm:text-base">
                  Fill out the form below and our team will get back to you{' '}
                  <span className="font-medium text-accent">within 24 hours</span>
                </p>
              </div>
            </div>
            <div
              aria-hidden="true"
              className="mt-5 h-[2px] w-16 rounded-full bg-gradient-to-r from-accent to-transparent"
            />

            <form
              name="contact"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
              className="mt-7 space-y-6"
            >
              <input type="hidden" name="form-name" value="contact" />
              <input type="hidden" name="bot-field" />

              <div className="grid gap-6 md:grid-cols-2">
                <div>
                  <label htmlFor="name" className="mb-2 block text-sm font-semibold text-slate-200">
                    Full Name *
                  </label>
                  <div className="relative">
                    <Icon
                      name="UserIcon"
                      size={18}
                      className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"
                    />
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className={inputClass}
                      placeholder="John Doe"
                    />
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="mb-2 block text-sm font-semibold text-slate-200"
                  >
                    Email Address *
                  </label>
                  <div className="relative">
                    <Icon
                      name="EnvelopeIcon"
                      size={18}
                      className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"
                    />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={inputClass}
                      placeholder="john@company.com"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label
                  htmlFor="company"
                  className="mb-2 block text-sm font-semibold text-slate-200"
                >
                  Company Name
                </label>
                <div className="relative">
                  <Icon
                    name="BuildingOfficeIcon"
                    size={18}
                    className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"
                  />
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="Your Company"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="inquiryType"
                  className="mb-2 block text-sm font-semibold text-slate-200"
                >
                  Inquiry Type *
                </label>
                <div className="relative">
                  <Icon
                    name="ClipboardDocumentListIcon"
                    size={18}
                    className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"
                  />
                  <select
                    id="inquiryType"
                    name="inquiryType"
                    value={formData.inquiryType}
                    onChange={handleChange}
                    required
                    className={`${inputClass} appearance-none pr-10 [&>option]:bg-[#0d1c36] [&>option]:py-2 [&>option]:text-slate-100`}
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="general">General Inquiry</option>
                    <option value="sample">Sample Request</option>
                    <option value="technical">Technical Consultation</option>
                    <option value="partnership">Partnership Opportunity</option>
                    <option value="support">Customer Support</option>
                  </select>
                  <Icon
                    name="ChevronDownIcon"
                    size={18}
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="mb-2 block text-sm font-semibold text-slate-200"
                >
                  Message *
                </label>
                <div className="relative">
                  <Icon
                    name="PencilIcon"
                    size={18}
                    className="pointer-events-none absolute left-4 top-4 text-slate-500"
                  />
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className={`${inputClass} resize-none`}
                    placeholder="Tell us about your project or inquiry..."
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="flex w-full items-center justify-center gap-3 rounded-lg bg-accent px-8 py-4 font-semibold uppercase tracking-wider text-white shadow-[0_2px_14px_rgba(14,165,233,0.35),inset_0_1px_0_rgba(255,255,255,0.2)] transition-[background-color,box-shadow,transform] duration-fast ease-out hover:bg-sky-400 hover:shadow-[0_6px_24px_rgba(14,165,233,0.45)] active:translate-y-px disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:active:transform-none"
              >
                {isSubmitting ? (
                  <>
                    <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Icon name="PaperAirplaneIcon" size={20} />
                    Send Message
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="flex items-center gap-3 rounded-lg border border-green-500/30 bg-green-500/10 p-4 text-green-400">
                  <Icon name="CheckCircleIcon" size={24} />
                  <span className="font-semibold">
                    Message sent successfully! We&rsquo;ll get back to you soon.
                  </span>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="flex items-center gap-3 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-red-400">
                  <Icon name="ExclamationCircleIcon" size={24} />
                  <span className="font-semibold">{submitError}</span>
                </div>
              )}
            </form>
          </div>

          {/* ── Quick Contact + Request a Sample ────────────────────────── */}
          <div data-reveal="right" className="min-w-0">
            <h3 className="text-2xl font-bold tracking-tight text-white sm:text-[1.75rem]">
              Quick Contact
            </h3>
            <div
              aria-hidden="true"
              className="mt-3 h-[2px] w-12 rounded-full bg-gradient-to-r from-accent to-transparent"
            />

            <div className="mt-6 space-y-4">
              <div
                className="group flex min-w-0 items-start gap-4 rounded-xl border border-accent/20 p-5 transition-[border-color,transform,box-shadow] duration-base ease-out hover:-translate-y-0.5 hover:border-accent/50 hover:shadow-[0_0_20px_rgba(14,165,233,0.1)] motion-reduce:hover:transform-none"
                style={cardSurface}
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-accent/40 bg-accent/10 text-accent transition-[border-color,box-shadow] duration-base ease-out group-hover:border-accent group-hover:shadow-[0_0_18px_rgba(14,165,233,0.25)]">
                  <Icon name="MapPinIcon" size={22} />
                </span>
                <div className="min-w-0">
                  <h4 className="font-semibold text-white">Location</h4>
                  <p className="mt-1 text-sm leading-relaxed text-slate-300">
                    Micratto Technologies Sdn. Bhd. (1280158T)
                    <br />
                    9, Jalan Seri Rejang 4
                    <br />
                    Pusat Industri Taman Sri Rampai
                    <br />
                    Setapak Jaya, 53300
                    <br />
                    Kuala Lumpur, Malaysia
                  </p>
                </div>
              </div>

              <div
                className="group flex min-w-0 items-start gap-4 rounded-xl border border-accent/20 p-5 transition-[border-color,transform,box-shadow] duration-base ease-out hover:-translate-y-0.5 hover:border-accent/50 hover:shadow-[0_0_20px_rgba(14,165,233,0.1)] motion-reduce:hover:transform-none"
                style={cardSurface}
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-accent/40 bg-accent/10 text-accent transition-[border-color,box-shadow] duration-base ease-out group-hover:border-accent group-hover:shadow-[0_0_18px_rgba(14,165,233,0.25)]">
                  <Icon name="EnvelopeIcon" size={22} />
                </span>
                <div className="min-w-0">
                  <h4 className="font-semibold text-white">Email Us</h4>
                  <a
                    href="mailto:operations@micratto.com"
                    className="mt-1 inline-block break-all text-sm font-medium text-accent transition-colors duration-fast hover:text-sky-300"
                  >
                    operations@micratto.com
                  </a>
                </div>
              </div>

              <div
                className="group flex min-w-0 items-start gap-4 rounded-xl border border-accent/20 p-5 transition-[border-color,transform,box-shadow] duration-base ease-out hover:-translate-y-0.5 hover:border-accent/50 hover:shadow-[0_0_20px_rgba(14,165,233,0.1)] motion-reduce:hover:transform-none"
                style={cardSurface}
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-accent/40 bg-accent/10 text-accent transition-[border-color,box-shadow] duration-base ease-out group-hover:border-accent group-hover:shadow-[0_0_18px_rgba(14,165,233,0.25)]">
                  <Icon name="GlobeAltIcon" size={22} />
                </span>
                <div className="min-w-0">
                  <h4 className="font-semibold text-white">Websites</h4>
                  <div className="mt-1 space-y-1.5">
                    <a
                      href="https://www.micratto.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block break-all text-sm font-medium text-accent transition-colors duration-fast hover:text-sky-300"
                    >
                      www.micratto.com
                    </a>
                    <a
                      href="https://www.infinoil.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block break-all text-sm font-medium text-accent transition-colors duration-fast hover:text-sky-300"
                    >
                      www.infinoil.com
                    </a>
                  </div>
                </div>
              </div>

              <div
                className="group flex min-w-0 items-start gap-4 rounded-xl border border-accent/20 p-5 transition-[border-color,transform,box-shadow] duration-base ease-out hover:-translate-y-0.5 hover:border-accent/50 hover:shadow-[0_0_20px_rgba(14,165,233,0.1)] motion-reduce:hover:transform-none"
                style={cardSurface}
              >
                <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-accent/40 bg-accent/10 text-accent transition-[border-color,box-shadow] duration-base ease-out group-hover:border-accent group-hover:shadow-[0_0_18px_rgba(14,165,233,0.25)]">
                  <Icon name="ClockIcon" size={22} />
                </span>
                <div className="min-w-0">
                  <h4 className="font-semibold text-white">Business Hours</h4>
                  <p className="mt-1 text-sm leading-relaxed text-slate-300">
                    Monday - Friday: 9:00 AM - 6:00 PM AEST
                    <br />
                    Saturday: 10:00 AM - 2:00 PM AEST
                    <br />
                    Sunday: Closed
                  </p>
                </div>
              </div>
            </div>

            {/* Request a Sample — decorative graphene render on the right,
                faded under a navy gradient so the copy stays readable. */}
            <div className="relative mt-6 overflow-hidden rounded-2xl border border-accent/40 shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_20px_50px_rgba(2,8,23,0.45),0_0_28px_rgba(14,165,233,0.1)]">
              <div aria-hidden="true" className="absolute inset-0 bg-[#0b1830]" />
              <div aria-hidden="true" className="absolute inset-y-0 right-0 w-3/5">
                <AppImage
                  src="/assets/images/graphene-special-feature-image.png"
                  alt=""
                  fill
                  sizes="(max-width: 1024px) 60vw, 28vw"
                  className="h-full w-full object-cover opacity-60 sm:opacity-80"
                />
                <div
                  aria-hidden="true"
                  className="absolute inset-0 bg-gradient-to-r from-[#0b1830] via-[#0b1830]/70 to-[#0b1830]/20"
                />
              </div>

              <div className="relative p-6 sm:p-7">
                <div className="flex items-center gap-3.5">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-accent/40 bg-accent/10 text-accent shadow-[0_0_18px_rgba(14,165,233,0.2),inset_0_0_12px_rgba(14,165,233,0.08)]">
                    <Icon name="BeakerIcon" size={24} />
                  </span>
                  <h3 className="text-xl font-bold tracking-tight text-white">Request a Sample</h3>
                </div>
                <p className="mt-4 max-w-[24rem] text-[0.9375rem] leading-relaxed text-slate-300">
                  Select &ldquo;Sample Request&rdquo; as your inquiry type to receive:
                </p>
                <ul className="mt-3 space-y-2 text-sm text-slate-300">
                  <li className="flex items-start gap-2">
                    <Icon
                      name="CheckCircleIcon"
                      size={18}
                      className="mt-0.5 flex-shrink-0 text-accent"
                    />
                    <span>Technical data sheets</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon
                      name="CheckCircleIcon"
                      size={18}
                      className="mt-0.5 flex-shrink-0 text-accent"
                    />
                    <span>Application guidelines</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon
                      name="CheckCircleIcon"
                      size={18}
                      className="mt-0.5 flex-shrink-0 text-accent"
                    />
                    <span>Sample kit for testing</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Icon
                      name="CheckCircleIcon"
                      size={18}
                      className="mt-0.5 flex-shrink-0 text-accent"
                    />
                    <span>Technical consultation</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
